import { DateTime } from 'luxon';
import * as React from 'react';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { useEffect, useState } from 'react';
import { NumberInputStyled } from '../NumberInput';
import styled from 'styled-components';
import ReactInputMask from 'react-input-mask';
import { mergeTests } from '../../test-utilities/mergeTests';
import { getTimeAsISO } from '../../utilities/getDateString';

export interface ITimeInputProps extends IFormCellProps {
  value: string | null;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (time: string | null) => void;
  onBlur?: (time: string | null) => void;
  suppressSeconds?: boolean;
  allowInvalidValues?: boolean;
}

export const TimeInput: React.FC<ITimeInputProps> = (props) => {
  const [inputValue, setInputValue] = useState<string | null>(null);
  const dataTestClass = props['data-test-class'] || 'time-input';

  useEffect(() => {
    setInputValue(getInputValue(props.value, props.suppressSeconds));
  }, [props.value, props.suppressSeconds]);
  const { formCellProps } = extractFormCellProps(props);

  const onTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.indexOf('_') === -1 && value.indexOf(':') !== -1 && value.indexOf(' ') !== -1) {
      const updatedValue = getTimeAsISO(value, props.suppressSeconds);
      props.onChange(updatedValue);
    } else {
      // If allowInvalidValues is true, pass the raw value instead of null
      props.onChange(props.allowInvalidValues ? value : null);
    }
    setInputValue(value);
  };

  const onTimeBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let newValue: string | null;
    if (value === '') {
      newValue = null;
    } else {
      const isoValue = getTimeAsISO(value, props.suppressSeconds);
      if (props.allowInvalidValues && !isoValue) {
        // If allowInvalidValues is true and getTimeAsISO returns null, pass the raw value
        newValue = value;
      } else {
        newValue = isoValue;
      }
    }

    if (props.onBlur) {
      props.onBlur(newValue);
    }
  };

  const getInputValue = (inputValue: string | null, suppressSeconds?: boolean): string => {
    if (!inputValue) return '';
    
    // If the value is already in display format (contains AM/PM), return it as is
    if (inputValue.includes('AM') || inputValue.includes('PM') || inputValue.includes('_')) {
      return inputValue;
    }
    
    // Try to convert from ISO format to display format
    try {
      const dateTime = DateTime.fromFormat(inputValue, suppressSeconds ? 'hh:mm' : 'hh:mm:ss').toLocaleString(
        DateTime.TIME_SIMPLE
      );
      if (dateTime.split(':')[0].length === 1) {
        return `0${dateTime}`;
      }
      return dateTime;
    } catch {
      // If conversion fails, return the original value
      return inputValue;
    }
  };

  return (
    <FormCell {...formCellProps}>
      <TimeInputStyled
        placeholder={props.placeholder}
        value={inputValue || ''}
        mask={'99:99 AM'}
        formatChars={{
          '*': '[A-Za-z0-9]',
          '9': '[0-9]',
          A: '[AaPp]',
          M: '[Mm]',
          a: '[A-Za-z]',
        }}
        onBlur={onTimeBlur}
        onChange={onTimeChange}
        showErrorHighlight={!!props.errorText}
        readOnly={props.readOnly}
        data-test-class={dataTestClass}
        data-test-id={mergeTests(props['data-test-id'], 'input')}
      />
    </FormCell>
  );
};

const TimeInputStyled = styled(NumberInputStyled.withComponent(ReactInputMask))`
  text-transform: uppercase;
`;
